// components/Cart.tsx
import { useState, useEffect } from 'react';
import {
  FaShoppingCart,
  FaTimes,
  FaPlus,
  FaMinus,
  FaTrash,
  FaArrowRight,
  FaTruck,
  FaShieldAlt,
  FaCheckCircle,
  FaInfoCircle,
} from 'react-icons/fa';

export interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
  size?: string;
  color?: string;
  shippingFee?: number; // Individual shipping fee per item
  freeShipping?: boolean; // Flag for free shipping
}

interface CartProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onUpdateQuantity: (id: number, quantity: number) => void;
  onRemoveItem: (id: number) => void;
  onCheckout: () => void;
  taxRate?: number;
}

export default function Cart({
  isOpen,
  onClose,
  items,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  taxRate = 0.08,
}: CartProps) {
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [shippingTotal, setShippingTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [freeShippingEligible, setFreeShippingEligible] = useState(false);

  useEffect(() => {
    // Calculate subtotal
    const newSubtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    
    // Calculate shipping total (per item shipping fee * quantity)
    const newShippingTotal = items.reduce((sum, item) => {
      // If item has free shipping flag or shipping fee is 0, no shipping cost
      if (item.freeShipping || !item.shippingFee) return sum;
      return sum + (item.shippingFee || 0) * item.quantity;
    }, 0);
    
    // Calculate tax (only on subtotal + shipping)
    const taxableAmount = newSubtotal + newShippingTotal;
    const newTax = taxableAmount * taxRate;
    
    // Check if free shipping threshold is met (optional)
    const isFreeShipping = newSubtotal > 100; // Example: free shipping over $100
    
    setSubtotal(newSubtotal);
    setShippingTotal(isFreeShipping ? 0 : newShippingTotal);
    setTax(newTax);
    setFreeShippingEligible(isFreeShipping);
    setTotal(newSubtotal + (isFreeShipping ? 0 : newShippingTotal) + newTax);
  }, [items, taxRate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FaShoppingCart className="text-blue-600 text-xl" />
            <h2 className="font-primary text-xl font-bold text-gray-800">Your Cart</h2>
            <span className="bg-blue-100 text-blue-600 text-xs font-bold px-2 py-1 rounded-full">
              {items.reduce((sum, item) => sum + item.quantity, 0)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <FaTimes className="text-gray-500" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[calc(100vh-320px)]">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🛒</div>
              <h3 className="font-primary text-xl font-bold text-gray-800 mb-2">Your cart is empty</h3>
              <p className="font-secondary text-gray-500">Start shopping to add items to your cart</p>
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            items.map((item) => {
              const itemShipping = item.freeShipping ? 0 : (item.shippingFee || 0);
              const hasShipping = itemShipping > 0 && !freeShippingEligible;
              
              return (
                <div key={item.id} className="flex gap-4 p-3 bg-gray-50 rounded-xl hover:shadow-md transition-all duration-200">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h4 className="font-primary font-semibold text-gray-800 text-sm">{item.name}</h4>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                      {item.color && <span>Color: {item.color}</span>}
                      {item.size && <span>Size: {item.size}</span>}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        <span className="font-primary font-bold text-blue-600">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                        {hasShipping && (
                          <span className="text-xs text-gray-400 ml-2">
                            +${(itemShipping * item.quantity).toFixed(2)} shipping
                          </span>
                        )}
                        {item.freeShipping && (
                          <span className="text-xs text-green-600 ml-2">🚚 Free shipping</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
                          className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors duration-200 flex items-center justify-center"
                        >
                          <FaMinus className="text-xs" />
                        </button>
                        <span className="font-primary font-semibold w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors duration-200 flex items-center justify-center"
                        >
                          <FaPlus className="text-xs" />
                        </button>
                        <button
                          onClick={() => onRemoveItem(item.id)}
                          className="w-7 h-7 rounded-full bg-red-50 hover:bg-red-100 text-red-500 transition-colors duration-200 flex items-center justify-center"
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Free Shipping Progress */}
        {items.length > 0 && (
          <div className="px-4 pb-2">
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              {freeShippingEligible ? (
                <span className="text-green-600 text-sm font-medium flex items-center justify-center gap-1">
                  <FaCheckCircle /> Free shipping applied! 🎉
                </span>
              ) : (
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Add ${(100 - subtotal).toFixed(2)} more for free shipping</span>
                    <span>{Math.min((subtotal / 100) * 100, 100).toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((subtotal / 100) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cart Summary */}
        {items.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-primary font-semibold text-gray-800">${subtotal.toFixed(2)}</span>
              </div>
              
              {/* Show individual shipping breakdown */}
              {!freeShippingEligible && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-1">
                    <FaTruck className="text-xs" /> Shipping
                  </span>
                  <span className="font-primary font-semibold text-gray-800">
                    ${shippingTotal.toFixed(2)}
                  </span>
                </div>
              )}
              
              {/* Show if free shipping is applied */}
              {freeShippingEligible && shippingTotal === 0 && subtotal > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-1 text-green-600">
                    <FaCheckCircle className="text-xs" /> Free Shipping
                  </span>
                  <span className="font-primary font-semibold text-green-600">$0.00</span>
                </div>
              )}
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax ({(taxRate * 100).toFixed(0)}%)</span>
                <span className="font-primary font-semibold text-gray-800">${tax.toFixed(2)}</span>
              </div>
              
              <div className="border-t border-gray-200 pt-2 flex justify-between">
                <span className="font-primary font-bold text-lg text-gray-800">Total</span>
                <span className="font-primary font-bold text-xl text-blue-600">${total.toFixed(2)}</span>
              </div>
            </div>
            
            <button
              onClick={onCheckout}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-primary font-semibold py-3 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              <FaArrowRight />
              Proceed to Checkout
            </button>
          </div>
        )}
      </div>
    </div>
  );
}